import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CheckboxField, RadioGroupField, SelectField, TextInputField } from '@brighte/form-components';
import { AddressType, BusinessDetailsType, EnterpriseStructure, MAX_FILE_SIZE_BYTES } from '@brighte/validation';
import { Address } from '@components/Address';
import { Box } from '@spark-web/box';
import { Columns } from '@spark-web/columns';
import { Dropzone } from '@spark-web/dropzone';
import { Field } from '@spark-web/field';
import { DocumentTextIcon, XIcon } from '@spark-web/icon';
import { RadioCard } from '@spark-web/radio';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { FormValues, useDeleteApplicationDocumentMutation } from '../../graphql';
import { useAbnLookup } from '../../hooks';
import { useApplicationData, useSetApplicationData } from '../../provider';
import { BUSINESS_DETAILS, LABELS } from '../../utils';

const PLEASE_SELECT = 'Please select';
const TRUST_DEED_REQUIRED = 'Trust deed is a required field';
const EMPTY_ARRAY: string[] = [];

type ExtendedBusinessDetailsProps = {
  formContext: UseFormReturn<BusinessDetailsType>;
};

function ExtendedBusinessDetailsFields({ formContext }: Readonly<ExtendedBusinessDetailsProps>) {
  const { control, setValue, watch, formState, getValues } = formContext;
  const businessDetails = useApplicationData('BusinessDetails');
  const applicationId = useApplicationData('applicationId');
  const setApplicationData = useSetApplicationData();
  const { mutate: deleteDocument } = useDeleteApplicationDocumentMutation();
  const billingContactSameAsPrimary = watch('enterprise.billingContactSameAsPrimary');
  const structure = watch('enterprise.structure');
  const isTrust = structure === EnterpriseStructure.Trust;
  const isPtyLtd = structure === EnterpriseStructure.ProprietaryLimitedCompany;

  const abnValue = isPtyLtd ? (watch('enterprise.abn') ?? null) : null;
  const { data: abnLookupResult } = useAbnLookup(abnValue);

  useEffect(() => {
    if (isPtyLtd) {
      setValue('enterprise.acnNumber' as Parameters<typeof setValue>[0], (abnLookupResult?.acn ?? '') as never);
    }
  }, [isPtyLtd, abnValue, abnLookupResult?.acn, setValue]);

  // Read trust deed document data from both the store and the form's default values.
  // The form remounts on Edit with defaultValues from the store, so getValues() reads the
  // initial defaults for fields that are not registered. This makes the display robust against
  // any timing issues with the store subscription.
  const storeDocumentIds = (businessDetails?.enterprise as { trustDeedDocumentIds?: string[] } | undefined)
    ?.trustDeedDocumentIds;
  const storeDocumentNames = (businessDetails?.enterprise as { trustDeedDocumentNames?: string[] } | undefined)
    ?.trustDeedDocumentNames;
  const formEnterprise = getValues('enterprise') as Record<string, unknown> | undefined;
  const formDocumentIds = formEnterprise?.trustDeedDocumentIds as string[] | undefined;
  const formDocumentNames = formEnterprise?.trustDeedDocumentNames as string[] | undefined;

  const existingDocumentIds = storeDocumentIds ?? formDocumentIds ?? EMPTY_ARRAY;
  const existingDocumentNames = storeDocumentNames ?? formDocumentNames ?? EMPTY_ARRAY;

  const isProcessingDrop = useRef(false);
  const trustDeedDocumentIdsSnapshotRef = useRef<string[] | undefined>();
  const trustDeedDocumentNamesSnapshotRef = useRef<string[] | undefined>();
  const hasExistingDocuments = existingDocumentIds.length > 0;

  // Keep a ref to businessDetails so handleTrustDeedFileDrop can read the latest
  // value without including it in useCallback dependencies. This prevents the
  // Dropzone from re-firing onChange when the store updates (which would wipe the
  // just-saved document IDs with undefined).
  const businessDetailsRef = useRef(businessDetails);
  businessDetailsRef.current = businessDetails;

  const [showExistingDocuments, setShowExistingDocuments] = useState(true);
  const [hasDroppedFiles, setHasDroppedFiles] = useState(false);
  const dropzoneContainerRef = useRef<HTMLDivElement>(null);

  // Trust deed field is not formally registered with react-hook-form (values are set via setValue),
  // so formState.errors may not always contain the zodResolver error. Use formState.isSubmitted
  // plus local state as a reliable fallback for displaying the required-field error.
  const resolverError = (formState.errors.enterprise as Record<string, { message?: string }> | undefined)
    ?.trustDeedFiles;
  const hasTrustDeed = hasDroppedFiles || (hasExistingDocuments && showExistingDocuments);
  const trustDeedError =
    resolverError ?? (formState.isSubmitted && !hasTrustDeed ? { message: TRUST_DEED_REQUIRED } : undefined);

  // The Spark Dropzone component declares onChange as ChangeEventHandler<HTMLInputElement>
  // but at runtime sends a synthetic event: { target: { value: File[], name? }, type: 'change' }.
  // We accept the declared type and extract files from target.value at runtime.
  const handleTrustDeedFileDrop = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // Guard against re-entrant calls from the Dropzone's useEffect
      if (isProcessingDrop.current) return;

      // Dropzone sends files via target.value (synthetic event with File[] instead of string).
      // Native file input change events (e.g. from Playwright setInputFiles) use target.files.
      const targetValue = event?.target?.value as unknown;
      const nativeFiles = (event?.target as unknown as HTMLInputElement)?.files;
      const files: File[] = Array.isArray(targetValue)
        ? targetValue
        : nativeFiles && nativeFiles.length > 0
          ? Array.from(nativeFiles)
          : [];

      // When Dropzone emits an empty array, treat it as the user clearing removed files.
      if (files.length === 0) {
        const currentBusinessDetails = businessDetailsRef.current;
        const currentEnterprise = currentBusinessDetails?.enterprise as
          | { trustDeedDocumentIds?: string[]; trustDeedDocumentNames?: string[] }
          | undefined;
        const existingDocumentIds = trustDeedDocumentIdsSnapshotRef.current ?? currentEnterprise?.trustDeedDocumentIds;
        const existingDocumentNames =
          trustDeedDocumentNamesSnapshotRef.current ?? currentEnterprise?.trustDeedDocumentNames;
        const refHasExistingDocuments = Array.isArray(existingDocumentIds) ? existingDocumentIds.length > 0 : false;
        setHasDroppedFiles(false);
        setShowExistingDocuments(refHasExistingDocuments);
        // Clear pending trust deed files from the form so validation can surface required errors.
        setTrustDeedFormValue('enterprise.trustDeedFiles', undefined, { shouldValidate: true });
        // Restore any existing uploaded docs from the snapshot.
        setApplicationData({
          BusinessDetails: {
            ...currentBusinessDetails,
            enterprise: {
              ...currentBusinessDetails?.enterprise,
              trustDeedFiles: undefined,
              trustDeedDocumentIds: existingDocumentIds,
              trustDeedDocumentNames: existingDocumentNames,
            },
          },
        } as Partial<FormValues>);
        return;
      }

      isProcessingDrop.current = true;
      // Hide existing documents when new files are selected (they will be replaced on save)
      setShowExistingDocuments(false);
      setHasDroppedFiles(true);

      const currentBusinessDetails = businessDetailsRef.current;
      const currentEnterprise = currentBusinessDetails?.enterprise as
        | { trustDeedDocumentIds?: string[]; trustDeedDocumentNames?: string[] }
        | undefined;
      // Snapshot existing document IDs/names so they can be restored if the user clears the new selection.
      trustDeedDocumentIdsSnapshotRef.current = currentEnterprise?.trustDeedDocumentIds
        ? [...currentEnterprise.trustDeedDocumentIds]
        : undefined;
      trustDeedDocumentNamesSnapshotRef.current = currentEnterprise?.trustDeedDocumentNames
        ? [...currentEnterprise.trustDeedDocumentNames]
        : undefined;

      // trustDeedFiles exists only on the Trust variant of the enterprise discriminated union.
      // react-hook-form can't resolve setValue paths across union variants, so we cast once here.
      // shouldValidate: true so the zodResolver clears any existing "required" error immediately.
      setTrustDeedFormValue('enterprise.trustDeedFiles', files, { shouldValidate: true });

      // Clear previous document IDs/names — new drops fully replace old uploads.
      setTrustDeedFormValue('enterprise.trustDeedDocumentIds', undefined, { shouldValidate: false });
      setTrustDeedFormValue('enterprise.trustDeedDocumentNames', undefined, { shouldValidate: false });

      // Also store in the application data so handleTrustDeedUpload can access them.
      // The form uses shouldUnregister: true, so unregistered fields set via setValue
      // are excluded from handleSubmit output.
      setApplicationData({
        BusinessDetails: {
          ...currentBusinessDetails,
          enterprise: {
            ...currentBusinessDetails?.enterprise,
            trustDeedFiles: files,
            trustDeedDocumentIds: undefined,
            trustDeedDocumentNames: undefined,
          },
        },
      } as Partial<FormValues>);

      // Allow future drops after this render cycle completes
      requestAnimationFrame(() => {
        isProcessingDrop.current = false;
      });
    },
    [setValue, setApplicationData, setHasDroppedFiles, setShowExistingDocuments],
  );

  const setTrustDeedFormValue = setValue as (
    name: string,
    value: unknown,
    options?: { shouldValidate: boolean },
  ) => void;

  const seededTrustDeedRef = useRef<{ ids?: string[]; names?: string[] } | null>(null);

  // Seed the form with existing document IDs when they become available,
  // and re-seed if the stored document IDs change. With shouldUnregister: true,
  // these fields are not registered DOM inputs, but setValue makes them
  // available in getValues() which the zodResolver reads during form submission.
  useEffect(() => {
    const hasExistingDocuments =
      (existingDocumentIds && existingDocumentIds.length > 0) ||
      (existingDocumentNames && existingDocumentNames.length > 0);
    if (!hasExistingDocuments) {
      return;
    }
    const arraysEqual = (first?: string[], second?: string[]) => {
      if (first === second) {
        return true;
      }
      if (!first || !second) {
        return false;
      }
      if (first.length !== second.length) {
        return false;
      }
      return first.every((value, index) => value === second[index]);
    };
    const previouslySeeded = seededTrustDeedRef.current;
    if (
      previouslySeeded &&
      arraysEqual(existingDocumentIds, previouslySeeded.ids) &&
      arraysEqual(existingDocumentNames, previouslySeeded.names)
    ) {
      return;
    }
    setTrustDeedFormValue('enterprise.trustDeedDocumentIds', existingDocumentIds, { shouldValidate: false });
    setTrustDeedFormValue('enterprise.trustDeedDocumentNames', existingDocumentNames, { shouldValidate: false });
    seededTrustDeedRef.current = {
      ids: existingDocumentIds,
      names: existingDocumentNames,
    };
  }, [existingDocumentIds, existingDocumentNames, setTrustDeedFormValue]);

  const handleRemoveExistingDocument = (indexToRemove: number) => {
    const removedDocumentId = existingDocumentIds[indexToRemove];
    const updatedIds = existingDocumentIds.filter((_, index) => index !== indexToRemove);
    const updatedNames = existingDocumentNames.filter((_, index) => index !== indexToRemove);

    // Snapshot the raw (pre-default) values so the rollback restores exact originals
    // rather than the EMPTY_ARRAY fallback.
    const snapshotIds = storeDocumentIds ?? formDocumentIds;
    const snapshotNames = storeDocumentNames ?? formDocumentNames;

    if (updatedIds.length === 0) {
      setShowExistingDocuments(false);
    }

    // Persist to both the application data store AND form state so the mutation
    // payload built from react-hook-form values reflects the removal.
    setTrustDeedFormValue('enterprise.trustDeedDocumentIds', updatedIds.length > 0 ? updatedIds : [], {
      shouldValidate: false,
    });
    setTrustDeedFormValue('enterprise.trustDeedDocumentNames', updatedNames.length > 0 ? updatedNames : [], {
      shouldValidate: false,
    });

    setApplicationData({
      BusinessDetails: {
        ...businessDetails,
        enterprise: {
          ...businessDetails?.enterprise,
          trustDeedDocumentIds: updatedIds.length > 0 ? updatedIds : undefined,
          trustDeedDocumentNames: updatedNames.length > 0 ? updatedNames : undefined,
        },
      },
    } as Partial<FormValues>);

    // Mark the document as Deleted in the backend so it doesn't remain as an
    // orphaned record. On failure, restore the document back into local state.
    if (applicationId && removedDocumentId) {
      deleteDocument(
        { deleteApplicationDocumentInput: { applicationId, documentId: removedDocumentId } },
        {
          onError: () => {
            setTrustDeedFormValue('enterprise.trustDeedDocumentIds', snapshotIds, {
              shouldValidate: false,
            });
            setTrustDeedFormValue('enterprise.trustDeedDocumentNames', snapshotNames, {
              shouldValidate: false,
            });
            setApplicationData({
              BusinessDetails: {
                ...businessDetails,
                enterprise: {
                  ...businessDetails?.enterprise,
                  trustDeedDocumentIds: snapshotIds,
                  trustDeedDocumentNames: snapshotNames,
                },
              },
            } as Partial<FormValues>);
            setShowExistingDocuments(true);
          },
        },
      );
    }
  };

  // The Dropzone renders its clickable area as <button> (without type="button"),
  // which defaults to type="submit" and would submit the outer form.
  // Fix: programmatically set type="button" on all buttons inside the Dropzone and
  // intercept submit events as a safety net.
  useEffect(() => {
    const container = dropzoneContainerRef.current;
    if (!container) return;

    // Set type="button" on any button inside the Dropzone that doesn't already have a type.
    const setButtonTypes = () => {
      container.querySelectorAll('button:not([type]), button[type="submit"]').forEach(button => {
        button.setAttribute('type', 'button');
      });
    };

    // Set on initial render
    setButtonTypes();

    // Watch for dynamically added buttons (e.g. after file selection)
    const observer = new MutationObserver(setButtonTypes);
    observer.observe(container, { childList: true, subtree: true });

    // Safety net: prevent form submissions originating from inside the Dropzone
    const form = container.closest('form');
    const handleSubmit = (event: SubmitEvent) => {
      if (event.submitter && container.contains(event.submitter)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    form?.addEventListener('submit', handleSubmit, true);

    return () => {
      observer.disconnect();
      form?.removeEventListener('submit', handleSubmit, true);
    };
  }, [isTrust]);

  const {
    LEGAL_BUSINESS_NAME,
    TRADING_NAME,
    ABN,
    BUSINESS_PHONE,
    BUSINESS_EMAIL,
    BUSINESS_WEBSITE,
    YEARS_IN_TRADING,
    NUMBER_OF_EMPLOYEES,
    INDUSTRY_CATEGORY,
    PROPERTY_STATUS,
    FIRST_NAME,
    MIDDLE_NAME,
    LAST_NAME,
    RESIDENTIAL_ADDRESS,
    BILLING_CONTACT,
    ACN_NUMBER,
  } = LABELS;

  const {
    EXTENDED_BUSINESS_DESCRIPTION,
    EXTENDED_PRIMARY_CONTACT_DESCRIPTION,
    EXTENDED_BILLING_CONTACT_DESCRIPTION,
    TRUST_DEED_TITLE,
    TRUST_DEED_DESCRIPTION,
    YEARS_IN_TRADING_OPTIONS,
    NUMBER_OF_EMPLOYEES_OPTIONS,
    INDUSTRY_CATEGORY_OPTIONS,
    PROPERTY_STATUS_OPTIONS,
    DECLARATION_SOLE_TRADER,
    DECLARATION_PARTNER,
    DECLARATION_TRUSTEE,
    DECLARATION_DIRECTOR,
  } = BUSINESS_DETAILS;

  const DECLARATION_TEXT_BY_STRUCTURE: Partial<Record<EnterpriseStructure, string>> = {
    [EnterpriseStructure.SoleTrader]: DECLARATION_SOLE_TRADER,
    [EnterpriseStructure.Partnership]: DECLARATION_PARTNER,
    [EnterpriseStructure.Trust]: DECLARATION_TRUSTEE,
    [EnterpriseStructure.ProprietaryLimitedCompany]: DECLARATION_DIRECTOR,
  };
  const declarationText = DECLARATION_TEXT_BY_STRUCTURE[structure];

  return (
    <>
      <Text>{EXTENDED_BUSINESS_DESCRIPTION}</Text>

      <TextInputField name="enterprise.legalEntityName" control={control} label={LEGAL_BUSINESS_NAME} />
      <TextInputField name="enterprise.tradingName" control={control} label={TRADING_NAME} />
      <TextInputField name="enterprise.abn" control={control} label={ABN} />
      {isPtyLtd && <TextInputField name="enterprise.acnNumber" control={control} label={ACN_NUMBER} />}
      <TextInputField name="enterprise.businessPhone" control={control} label={BUSINESS_PHONE} />
      <TextInputField name="enterprise.businessEmail" control={control} label={BUSINESS_EMAIL} />
      <TextInputField
        name="enterprise.businessWebsite"
        control={control}
        label={BUSINESS_WEBSITE}
        FieldProps={{ secondaryLabel: '(Optional)' }}
      />

      <Columns gap="xxlarge">
        <SelectField
          name="enterprise.yearsInTrading"
          control={control}
          label={YEARS_IN_TRADING}
          options={YEARS_IN_TRADING_OPTIONS}
          placeholder={PLEASE_SELECT}
        />
        <SelectField
          name="enterprise.numberOfEmployees"
          control={control}
          label={NUMBER_OF_EMPLOYEES}
          options={NUMBER_OF_EMPLOYEES_OPTIONS}
          placeholder={PLEASE_SELECT}
        />
      </Columns>

      <SelectField
        name="enterprise.industryCategory"
        control={control}
        label={INDUSTRY_CATEGORY}
        options={INDUSTRY_CATEGORY_OPTIONS}
        placeholder={PLEASE_SELECT}
      />
      <SelectField
        name="enterprise.propertyStatus"
        control={control}
        label={PROPERTY_STATUS}
        options={PROPERTY_STATUS_OPTIONS}
        placeholder={PLEASE_SELECT}
      />

      {isTrust && (
        <Field
          label={TRUST_DEED_TITLE}
          tone={trustDeedError ? 'critical' : undefined}
          message={trustDeedError?.message}
        >
          <Text>{TRUST_DEED_DESCRIPTION}</Text>

          <Box ref={dropzoneContainerRef}>
            <Dropzone
              accept={{
                'image/jpeg': ['.jpeg', '.jpg'],
                'image/png': ['.png'],
                'application/pdf': ['.pdf'],
              }}
              onChange={handleTrustDeedFileDrop}
              maxFileSizeKb={MAX_FILE_SIZE_BYTES / 1024}
              maxFiles={0}
              multiple={true}
            />
          </Box>

          {hasExistingDocuments && showExistingDocuments && (
            <Stack as="ul" role="list" gap="medium">
              {existingDocumentIds.map((documentId, index) => {
                const filename = existingDocumentNames[index] ?? `Document ${index + 1}`;
                return (
                  <Box key={documentId} as="li" border="field" borderRadius="small" padding="xsmall">
                    <Box display="flex" alignItems="center" gap="medium" height="medium" paddingLeft="small">
                      <DocumentTextIcon size="xsmall" />
                      <Box flex={1}>
                        <Text inline>{filename}</Text>
                      </Box>
                      <Box
                        as="button"
                        type="button"
                        onClick={() => handleRemoveExistingDocument(index)}
                        aria-label={`Remove ${filename}`}
                        cursor="pointer"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="small"
                        height="medium"
                        width="medium"
                      >
                        <XIcon size="xxsmall" />
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Field>
      )}

      <Text>{EXTENDED_PRIMARY_CONTACT_DESCRIPTION}</Text>

      <TextInputField name="enterprise.primaryContact.firstName" control={control} label={FIRST_NAME} />
      <TextInputField
        name="enterprise.primaryContact.middleName"
        control={control}
        label={MIDDLE_NAME}
        FieldProps={{ secondaryLabel: '(Optional)' }}
      />
      <TextInputField name="enterprise.primaryContact.lastName" control={control} label={LAST_NAME} />

      <Address<BusinessDetailsType>
        control={control}
        name="enterprise.primaryContact.residentialAddress"
        label={RESIDENTIAL_ADDRESS}
        setValue={setValue}
        defaultValue={
          (businessDetails?.enterprise as { primaryContact?: { residentialAddress?: AddressType } } | undefined)
            ?.primaryContact?.residentialAddress
        }
      />

      <RadioGroupField
        name="enterprise.billingContactSameAsPrimary"
        control={control}
        label={BILLING_CONTACT}
        FieldProps={{ description: EXTENDED_BILLING_CONTACT_DESCRIPTION }}
      >
        <Columns gap="large">
          <RadioCard value={true}>
            <Text>Yes</Text>
          </RadioCard>
          <RadioCard value={false}>
            <Text>No</Text>
          </RadioCard>
        </Columns>
      </RadioGroupField>

      {billingContactSameAsPrimary === false && (
        <>
          <TextInputField name="enterprise.billingContact.firstName" control={control} label={FIRST_NAME} />
          <TextInputField
            name="enterprise.billingContact.middleName"
            control={control}
            label={MIDDLE_NAME}
            FieldProps={{ secondaryLabel: '(Optional)' }}
          />
          <TextInputField name="enterprise.billingContact.lastName" control={control} label={LAST_NAME} />

          <Address<BusinessDetailsType>
            control={control}
            name="enterprise.billingContact.residentialAddress"
            label={RESIDENTIAL_ADDRESS}
            setValue={setValue}
            defaultValue={
              (businessDetails?.enterprise as { billingContact?: { residentialAddress?: AddressType } } | undefined)
                ?.billingContact?.residentialAddress
            }
          />
        </>
      )}

      {declarationText && <CheckboxField name="customerDeclared" control={control} label={declarationText} />}
    </>
  );
}

export default ExtendedBusinessDetailsFields;
